/**
 * store/player.ts — the «плеер» slice: what is playing, and how it is going.
 *
 * Task 05 drew the boundary, task 07 fills it. `spec.md` «Плеер»: the store is split into
 * slices «чтобы перерисовка списка не перерисовывала плеер». Nothing here is imported from or
 * by `store/library.ts` or `store/queue.ts`: a page of three thousand rows landing in the
 * library notifies no subscriber of this store, and the four updates a second of a playing
 * track notify nobody who draws the list or the queue. What plays next is the queue's
 * business (task 08); this slice knows only the track it holds.
 *
 * The state is written by exactly one module, `engine/player.ts`, from the element's own
 * events; the components read it and call the controller's methods. Nothing in React ever
 * touches the element.
 */

import { create } from 'zustand';

import type { TrackItem } from '../lib/api';

/**
 * - `idle` — nothing has been played yet;
 * - `loading` — `play()` was called and no sound has come yet;
 * - `playing`, `paused` — what they say;
 * - `ended` — the last track of the queue played to its end, and nothing follows it. Any other
 *   end is the next track's `loading` (task 08).
 */
export type PlayerStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'ended';

/** The listener asked for sound and is getting or awaiting it: the button shows «Пауза». */
export function isSounding(status: PlayerStatus): boolean {
  return status === 'playing' || status === 'loading';
}

/**
 * Why the player needs the listener, if it does:
 *
 * - `gesture` — the phone refused to start without a tap (`NotAllowedError`): «Играть»;
 * - `stalled` — twelve seconds without progress: «Повторить»;
 * - `failed` — the element gave up on the file: «Повторить»;
 * - `gone` — the track's post left the channel: nothing to retry.
 */
export type PlayerProblem = 'gesture' | 'stalled' | 'failed' | 'gone';

export interface PlayerState {
  /** The track the player holds, by id. `undefined` while nothing has been played. */
  readonly trackId: number | undefined;
  readonly track: TrackItem | undefined;
  readonly status: PlayerStatus;
  /** The element is waiting for data. Shown as the loading indicator. */
  readonly buffering: boolean;
  readonly problem: PlayerProblem | undefined;
  readonly positionS: number;
  /** The catalogue's duration at first, the element's once it knows it. `0` is unknown. */
  readonly durationS: number;
  /** How far the slider may go: the whole track, or the part that could be loaded. */
  readonly seekLimitS: number;
  /**
   * Task 49: the second a long track was started at because the listener stopped there last
   * time — «Загружается… с 42:10». `undefined` for a start from the beginning.
   */
  readonly resumeFromS: number | undefined;
  /**
   * Task 45: one track is going into the next — both decks sound. The status row says «ПЕРЕХОД»,
   * and there is no scratch until it is over.
   */
  readonly crossfading: boolean;
  /**
   * Task 47: that overlap is a mix — the next track brought to the playing one's tempo and beats.
   * The status row says «МИКС» instead of «ПЕРЕХОД».
   */
  readonly mixing: boolean;
  /**
   * Task 48: the sleep timer — when it falls (unix ms), `'track'` for the end of the track playing,
   * `undefined` when off. Kept only for this launch (`spec.md` story 134).
   */
  readonly sleepAt: number | 'track' | undefined;
  /** Task 48: whole seconds the timer has left, as the player's interval last saw them. */
  readonly sleepLeftS: number | undefined;
}

export const IDLE_PLAYER: PlayerState = Object.freeze({
  trackId: undefined,
  track: undefined,
  status: 'idle',
  buffering: false,
  problem: undefined,
  positionS: 0,
  durationS: 0,
  seekLimitS: 0,
  resumeFromS: undefined,
  crossfading: false,
  mixing: false,
  sleepAt: undefined,
  sleepLeftS: undefined,
});

export const usePlayer = create<PlayerState>(() => IDLE_PLAYER);
